import { generatePath } from 'react-router-dom';
import { Flex, Skeleton, Text } from '@mantine/core';
import {
	useTranslationCapManage,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { formatAuditActionToDisplay } from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { InvestorDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.tsx';
import {
	RECENT_ACTIVITY_SKELETON_HEIGHT,
	RecentActivityList,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/RecentActivityCard/RecentActivityCard.styles.tsx';
import { useInvestorRecentActivity } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/RecentActivityCard/useInvestorRecentActivity.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';

interface Props {
	dealId: string;
	personId?: string;
	isLoading: boolean;
}

export const RecentActivityCard = ({ dealId, personId, isLoading }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCapManage = useTranslationCapManage();
	const { audits, isLoading: isLoadingAudits, isError } = useInvestorRecentActivity(personId);

	if (isLoading || isLoadingAudits) {
		return <Skeleton height={RECENT_ACTIVITY_SKELETON_HEIGHT} radius="0.5rem" />;
	}

	const historyPath = personId
		? generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_HISTORY}`, {
				dealId,
				personId,
			})
		: undefined;

	const renderMessage = (message: string) => (
		<Flex justify="center" align="center" className="empty">
			<Text c="gray.9">{message}</Text>
		</Flex>
	);

	return (
		<InvestorDetailsCard
			title={tMipManage('investors.details.recent_activity.title', {
				defaultValue: 'Recent activity',
			})}
			action={historyPath ? { kind: 'link', to: historyPath } : undefined}
			dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_RECENT_ACTIVITY_CARD}
		>
			<RecentActivityList>
				{isError &&
					renderMessage(
						tMipManage('investors.details.recent_activity.error', {
							defaultValue: 'Recent activity could not be loaded.',
						})
					)}

				{!isError &&
					audits.length === 0 &&
					renderMessage(
						tMipManage('investors.details.recent_activity.empty', {
							defaultValue: 'No recent activity.',
						})
					)}

				{audits.map((audit) => (
					<div
						key={audit.id}
						className="entry"
						data-testid={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_RECENT_ACTIVITY_ENTRY
						}
					>
						<div className="icon">
							<Icon variant="checkmark" size="md" />
						</div>

						<div className="labels">
							<Text fw={600} c="dark.8" mb="0.25rem">
								{tMipManage('investors.details.recent_activity.entry', {
									action: formatAuditActionToDisplay(audit.action, tCapManage),
									actor:
										audit.userDisplayName ??
										tCapManage('people.history.system_actor'),
									defaultValue: '{{action}} by {{actor}}',
								})}
							</Text>

							<Text c="dark.4">
								<span className="activity-bullet"></span>

								{formatStringDateToUI(
									audit.createdAt,
									DATE_FORMATS.TIMELINE_DATE_TIME
								)}
							</Text>
						</div>
					</div>
				))}
			</RecentActivityList>
		</InvestorDetailsCard>
	);
};
